import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Truck, IndianRupee, Share2, Link2, CheckCircle2, AlertTriangle, KeyRound, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const L = ({ label, children }) => (
  <label className="block"><span className="text-xs font-semibold text-muted2 uppercase tracking-wide">{label}</span>{children}</label>
);

function ChangePinCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const inputCls = "w-full h-11 rounded-lg border border-line px-3 text-sm mt-1 focus:outline-none focus:border-brand";

  const submit = async () => {
    if (!current || !next) { toast.error("Fill in all PIN fields"); return; }
    if (next.length < 6) { toast.error("New PIN must be at least 6 digits"); return; }
    if (!/^\d+$/.test(next)) { toast.error("New PIN must contain digits only"); return; }
    if (next !== confirm) { toast.error("New PIN and confirmation do not match"); return; }
    setBusy(true);
    try {
      await api.changeAdminPin(current, next);
      toast.success("Admin PIN changed. Use your new PIN next time you log in.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  return (
    <section className="rounded-2xl border border-line p-5 mb-5">
      <h3 className="font-display text-xl text-ink flex items-center gap-2 mb-1"><KeyRound className="text-brand" size={18} /> Change Admin PIN</h3>
      <p className="text-xs text-muted2 mb-4">Set your own admin login PIN (minimum 6 digits). You'll be logged out of other sessions after changing it.</p>
      <div className="grid sm:grid-cols-3 gap-3 max-w-xl">
        <L label="Current PIN"><input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className={inputCls} data-testid="pin-current" /></L>
        <L label="New PIN"><input type="password" value={next} onChange={(e) => setNext(e.target.value)} className={inputCls} data-testid="pin-new" /></L>
        <L label="Confirm New PIN"><input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} data-testid="pin-confirm" /></L>
      </div>
      <button onClick={submit} disabled={busy} className="mt-4 h-11 px-5 rounded-full bg-brand text-white font-semibold flex items-center gap-2 disabled:opacity-60" data-testid="pin-change-submit">
        {busy ? <Loader2 className="animate-spin" size={18} /> : "Update PIN"}
      </button>
    </section>
  );
}

function TwilioCard() {
  const qc = useQueryClient();
  const { data: status } = useQuery({ queryKey: ["twilio-status"], queryFn: () => api.twilioStatus() });
  const [sid, setSid] = useState("");
  const [token, setToken] = useState("");
  const [from, setFrom] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [tmpl, setTmpl] = useState({ tmpl_shipped: "", tmpl_delivered: "", tmpl_new_order: "" });
  const [busy, setBusy] = useState(false);
  const inputCls = "w-full h-11 rounded-lg border border-line px-3 text-sm mt-1 focus:outline-none focus:border-brand";

  useEffect(() => {
    if (status?.templates) setTmpl({
      tmpl_shipped: status.templates.tmpl_shipped || "",
      tmpl_delivered: status.templates.tmpl_delivered || "",
      tmpl_new_order: status.templates.tmpl_new_order || "",
    });
  }, [status]);

  const connect = async () => {
    if (!sid || !token || !from) { toast.error("Enter Account SID, Auth Token and WhatsApp sender number"); return; }
    setBusy(true);
    try {
      await api.twilioConnect(sid.trim(), token.trim(), from.trim());
      toast.success("Twilio WhatsApp connected");
      setToken("");
      qc.invalidateQueries({ queryKey: ["twilio-status"] });
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true);
    try { await api.twilioDisconnect(); toast.success("Disconnected"); qc.invalidateQueries({ queryKey: ["twilio-status"] }); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const sendTest = async () => {
    if (!testPhone) { toast.error("Enter a phone number to test"); return; }
    setBusy(true);
    try { await api.twilioTest(testPhone.trim()); toast.success("Test message sent — check WhatsApp"); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const saveTemplates = async () => {
    setBusy(true);
    try {
      await api.twilioSaveTemplates(tmpl.tmpl_shipped.trim(), tmpl.tmpl_delivered.trim(), tmpl.tmpl_new_order.trim());
      toast.success("Template SIDs saved");
      qc.invalidateQueries({ queryKey: ["twilio-status"] });
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const setT = (k) => (e) => setTmpl((p) => ({ ...p, [k]: e.target.value }));

  return (
    <section className="rounded-2xl border border-line p-5 mb-5">
      <h3 className="font-display text-xl text-ink flex items-center gap-2 mb-1"><MessageCircle className="text-brand" size={18} /> WhatsApp Delivery Notifications</h3>
      <p className="text-xs text-muted2 mb-4">Connect Twilio to auto-message customers on WhatsApp when their order is shipped or delivered, and alert the owner on new orders. Get your Account SID &amp; Auth Token from the Twilio Console, and a WhatsApp-enabled sender number.</p>
      {status?.connected ? (
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-ok" data-testid="twilio-connected">
            <CheckCircle2 size={16} /> Connected
          </div>
          <p className="text-xs text-muted2 mt-1">Sender: {status.from_number}</p>

          <div className="mt-4 rounded-xl bg-mist/60 border border-line p-3">
            <p className="text-xs font-semibold text-ink mb-1">Approved WhatsApp Template SIDs (Content SIDs, start with HX)</p>
            <p className="text-[11px] text-muted2 mb-3">Business-initiated WhatsApp messages require approved templates. Create them in Twilio → Messaging → Content Template Builder, then paste each template's Content SID below. Leave blank to send plain text (only works within a 24h session / sandbox).</p>
            <div className="space-y-2">
              <div>
                <span className="text-[11px] font-semibold text-muted2">Order Shipped — vars: name, order #, tracking link</span>
                <input value={tmpl.tmpl_shipped} onChange={setT("tmpl_shipped")} placeholder="HX… (order_shipped)" className={inputCls} data-testid="tmpl-shipped" />
              </div>
              <div>
                <span className="text-[11px] font-semibold text-muted2">Order Delivered — vars: name, order #</span>
                <input value={tmpl.tmpl_delivered} onChange={setT("tmpl_delivered")} placeholder="HX… (order_delivered)" className={inputCls} data-testid="tmpl-delivered" />
              </div>
              <div>
                <span className="text-[11px] font-semibold text-muted2">New Order (owner) — vars: order #, customer, phone, item count, total</span>
                <input value={tmpl.tmpl_new_order} onChange={setT("tmpl_new_order")} placeholder="HX… (new_order_store_owner)" className={inputCls} data-testid="tmpl-new-order" />
              </div>
            </div>
            <button onClick={saveTemplates} disabled={busy} className="mt-3 h-10 px-4 rounded-full bg-brand text-white text-sm font-semibold disabled:opacity-60" data-testid="twilio-save-templates">Save template SIDs</button>
          </div>

          <div className="grid sm:grid-cols-[1fr_auto] gap-3 mt-4 max-w-lg items-end">
            <L label="Send test to (phone)"><input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="10-digit mobile" className={inputCls} data-testid="twilio-test-phone" /></L>
            <button onClick={sendTest} disabled={busy} className="h-11 px-4 rounded-full border border-line text-sm font-semibold text-brand disabled:opacity-60" data-testid="twilio-test-send">Send test</button>
          </div>
          <button onClick={disconnect} disabled={busy} className="mt-3 h-10 px-4 rounded-full border border-line text-sm font-semibold text-err disabled:opacity-60" data-testid="twilio-disconnect">Disconnect</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3 max-w-lg">
          <input value={sid} onChange={(e) => setSid(e.target.value)} placeholder="Account SID (AC…)" className={inputCls} data-testid="twilio-sid" />
          <input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Auth Token" className={inputCls} data-testid="twilio-token" />
          <input value={from} onChange={(e) => setFrom(e.target.value)} placeholder="WhatsApp sender e.g. +14155238886" className={`${inputCls} sm:col-span-2`} data-testid="twilio-from" />
          <button onClick={connect} disabled={busy} className="sm:col-span-2 h-11 rounded-full bg-brand text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="twilio-connect">
            {busy ? <Loader2 className="animate-spin" size={18} /> : "Connect Twilio WhatsApp"}
          </button>
        </div>
      )}
    </section>
  );
}

function ShiprocketCard() {
  const qc = useQueryClient();
  const { data: status } = useQuery({ queryKey: ["shiprocket-status"], queryFn: () => api.shiprocketStatus() });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const inputCls = "w-full h-11 rounded-lg border border-line px-3 text-sm mt-1 focus:outline-none focus:border-brand";

  const connect = async () => {
    if (!email || !password) { toast.error("Enter Shiprocket API email & password"); return; }
    setBusy(true);
    try {
      const r = await api.shiprocketConnect(email, password);
      toast.success(r.warning || "Shiprocket connected");
      setPassword("");
      qc.invalidateQueries({ queryKey: ["shiprocket-status"] });
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true);
    try { await api.shiprocketDisconnect(); toast.success("Disconnected"); qc.invalidateQueries({ queryKey: ["shiprocket-status"] }); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  return (
    <section className="rounded-2xl border border-line p-5 mb-5">
      <h3 className="font-display text-xl text-ink flex items-center gap-2 mb-1"><Link2 className="text-brand" size={18} /> Shiprocket</h3>
      <p className="text-xs text-muted2 mb-4">Connect your Shiprocket API user (Shiprocket Panel → Settings → API → Configure). Then use "Sync Shiprocket" in the Orders tab to pull tracking & delivery status into your orders.</p>
      {status?.connected ? (
        <div>
          <div className={`flex items-center gap-2 text-sm font-semibold ${status.verified ? "text-ok" : "text-warn"}`} data-testid="shiprocket-connected">
            {status.verified ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
            {status.verified ? "Connected & verified" : "Saved (verifies after deployment)"}
          </div>
          <p className="text-xs text-muted2 mt-1">{status.email}</p>
          <button onClick={disconnect} disabled={busy} className="mt-3 h-10 px-4 rounded-full border border-line text-sm font-semibold text-err disabled:opacity-60" data-testid="shiprocket-disconnect">Disconnect</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3 max-w-lg">
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="API user email" className={inputCls} data-testid="shiprocket-email" />
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="API user password" className={inputCls} data-testid="shiprocket-password" />
          <button onClick={connect} disabled={busy} className="sm:col-span-2 h-11 rounded-full bg-brand text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="shiprocket-connect">
            {busy ? <Loader2 className="animate-spin" size={18} /> : "Connect Shiprocket"}
          </button>
        </div>
      )}
    </section>
  );
}

export default function SettingsAdmin() {
  const { data, isLoading, refetch } = useQuery({ queryKey: ["settings"], queryFn: () => api.getSettings() });
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data) setF(data); }, [data]);
  if (isLoading || !f) return <p className="text-muted2">Loading…</p>;

  const set = (k, num) => (e) => setF((s) => ({ ...s, [k]: num ? Number(e.target.value) || 0 : e.target.value }));
  const inputCls = "w-full h-11 rounded-lg border border-line px-3 text-sm mt-1 focus:outline-none focus:border-brand";

  const save = async () => {
    setSaving(true);
    try { await api.updateSettings(f); toast.success("Settings saved"); refetch(); }
    catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl">
      <h2 className="font-display text-2xl text-ink mb-5">Store Settings</h2>

      <section className="rounded-2xl border border-line p-5 mb-5">
        <h3 className="font-display text-xl text-ink flex items-center gap-2 mb-4"><Truck className="text-brand" size={18} /> Shipping</h3>
        <div className="flex gap-2 mb-4">
          {[["weight", "Weight-based tiers"], ["flat", "Flat rate"]].map(([v, l]) => (
            <button key={v} onClick={() => setF((s) => ({ ...s, shipping_mode: v }))} className={`h-10 px-4 rounded-full text-sm font-semibold border ${f.shipping_mode === v ? "bg-brand text-white border-brand" : "border-line text-muted2"}`} data-testid={`shipping-mode-${v}`}>{l}</button>
          ))}
        </div>
        {f.shipping_mode === "weight" ? (
          <div className="grid sm:grid-cols-2 gap-3">
            <L label="Free up to (grams)"><input type="number" value={f.free_upto_grams} onChange={set("free_upto_grams", true)} className={inputCls} /></L>
            <L label="Mid tier up to (grams)"><input type="number" value={f.mid_upto_grams} onChange={set("mid_upto_grams", true)} className={inputCls} /></L>
            <L label="Mid tier charge ₹"><input type="number" value={f.mid_charge} onChange={set("mid_charge", true)} className={inputCls} /></L>
            <L label="High tier charge ₹"><input type="number" value={f.high_charge} onChange={set("high_charge", true)} className={inputCls} /></L>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            <L label="Flat charge ₹"><input type="number" value={f.flat_charge} onChange={set("flat_charge", true)} className={inputCls} /></L>
            <L label="Free above amount ₹ (0 = never)"><input type="number" value={f.free_above_amount} onChange={set("free_above_amount", true)} className={inputCls} /></L>
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-line p-5 mb-5">
        <h3 className="font-display text-xl text-ink flex items-center gap-2 mb-4"><IndianRupee className="text-brand" size={18} /> Minimum Order Value</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <L label="Regular (guest) ₹"><input type="number" value={f.min_purchase_regular} onChange={set("min_purchase_regular", true)} className={inputCls} data-testid="min-regular" /></L>
          <L label="Altos ID holders ₹"><input type="number" value={f.min_purchase_altos} onChange={set("min_purchase_altos", true)} className={inputCls} data-testid="min-altos" /></L>
        </div>
      </section>

      <section className="rounded-2xl border border-line p-5 mb-5">
        <h3 className="font-display text-xl text-ink flex items-center gap-2 mb-4"><Share2 className="text-brand" size={18} /> Social Media Links</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <L label="YouTube URL"><input value={f.youtube_url} onChange={set("youtube_url")} className={inputCls} data-testid="social-youtube" /></L>
          <L label="Facebook URL"><input value={f.facebook_url} onChange={set("facebook_url")} className={inputCls} data-testid="social-facebook" /></L>
          <L label="Instagram URL"><input value={f.instagram_url} onChange={set("instagram_url")} className={inputCls} data-testid="social-instagram" /></L>
          <L label="X (Twitter) URL"><input value={f.x_url} onChange={set("x_url")} className={inputCls} data-testid="social-x" /></L>
          <L label="Android App URL (Play Store / APK)"><input value={f.android_app_url} onChange={set("android_app_url")} placeholder="https://play.google.com/store/apps/details?id=…" className={inputCls} data-testid="android-app-url" /></L>
        </div>
      </section>

      <section className="rounded-2xl border border-line p-5 mb-5">
        <h3 className="font-display text-xl text-ink flex items-center gap-2 mb-1"><MessageCircle className="text-brand" size={18} /> New-Order Alerts</h3>
        <p className="text-xs text-muted2 mb-4">Get a WhatsApp message every time a new order is placed. Requires Twilio WhatsApp to be connected (below). Leave blank to turn off.</p>
        <L label="Your WhatsApp number (owner)"><input value={f.owner_whatsapp || ""} onChange={set("owner_whatsapp")} placeholder="e.g. 9876543210" className={inputCls} data-testid="owner-whatsapp" /></L>
      </section>

      <ShiprocketCard />

      <TwilioCard />

      <ChangePinCard />

      <button disabled={saving} onClick={save} className="h-11 px-6 rounded-full bg-brand text-white font-semibold flex items-center gap-2 disabled:opacity-60" data-testid="settings-save">
        {saving ? <Loader2 className="animate-spin" size={18} /> : "Save settings"}
      </button>
    </div>
  );
}
